// ============================================
// PIPELINE MONITOR - Global event bus for AI pipeline telemetry
// ============================================
// Records every AI pipeline execution with input/output data, timing, and errors.
// Activated on app start. All methods are safe to call - never throws, never blocks.
// Persists completed executions to DataService for historical analysis.

import { DataService } from './data-service.js';
import { safeWriteFF } from './safe-write.js';
import { MODEL_PRICING } from './benchmark-configs.js';
import { isEvalAiSingleAttemptPolicyActive } from './eval-ai-single-attempt-policy';
import { runWhenBackendAnswers } from './network-resilience';

// Telemetry is background work. While QuoteMaker's server is not answering the
// row is held (the latest status per pipeline step) and written once when it
// answers, instead of one failing write per AI step (outage lane, 2026-09-29).
// While the server answers this runs the write at once, exactly as before.
function _persistPipelineRow(entryId, write) {
    runWhenBackendAnswers(`pipeline:${entryId}`, write, { label: 'pipeline telemetry' });
}

const MAX_ENTRIES = 200;
const ACTIVE_SESSION_STORAGE_KEY = 'qm_pipeline_monitor_active_v1';
const ACTIVE_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Workstream E3 (2026-04-24): 200k-char cap before we send prompt / response
// to the backend. TEXT columns have no size cap at the DB layer but we refuse
// to ship gargantuan bodies across the wire on every telemetry write. Anything
// larger gets a truncation tombstone so the failed-run exporter can still
// reconstruct the original length.
const DB_PROMPT_MAX_CHARS = 200000;
const DB_PROMPT_SAFE_CHARS = 199500; // leave headroom for the tombstone suffix
const FAILURE_IDENTIFIER_MAX_CHARS = 96;
const OPERATOR_ACTION_MAX_CHARS = 500;
const RETRY_AFTER_MAX_SECONDS = 86_400;
const SENSITIVE_AUDIT_KEY_RE = /^(?:authorization|body|cause|credential|credentials|detail|error|headers|message|password|raw|rawbody|rawerror|rawresponse|reason|request|response|secret|stack|token)$/i;
const CREDENTIAL_SHAPED_VALUE_RE = /(?:\bbearer\s+[a-z0-9._-]{8,}|\b(?:api[_ -]?key|password|secret|token)\s*[:=]\s*\S+|\bsk-(?:or-)?[a-z0-9_-]{8,})/i;
const INLINE_CUSTOMER_MEDIA_DATA_URL_RE = /data:(?:image|video)\/[^,;"'\\\s]+(?:;[^,;"'\\\s]+)*;base64,[a-z0-9+/_=-]+/gi;
const INLINE_CUSTOMER_MEDIA_BASE64_KEY_RE = /^(?:image|video|proxy|thumbnail|poster)[_-]?base64$/i;
const INLINE_CUSTOMER_MEDIA_BASE64_PREFIX_RE = /^(?:\/9j\/|iVBORw0KGgo|R0lGOD|UklGR|AAAA[A-Za-z0-9+/_-]{4,}ftyp)/;
const INLINE_CUSTOMER_MEDIA_OMITTED = '[inline customer media omitted]';

function _sanitiseTelemetryString(value, key = '') {
    if (typeof value !== 'string') return value;
    if (INLINE_CUSTOMER_MEDIA_BASE64_KEY_RE.test(key)
        || INLINE_CUSTOMER_MEDIA_BASE64_PREFIX_RE.test(value.trim())) {
        return INLINE_CUSTOMER_MEDIA_OMITTED;
    }
    return value.replace(INLINE_CUSTOMER_MEDIA_DATA_URL_RE, INLINE_CUSTOMER_MEDIA_OMITTED);
}

function _stringifyTelemetryValue(value) {
    try {
        return JSON.stringify(value, (key, nested) => {
            if (typeof nested === 'string') return _sanitiseTelemetryString(nested, key);
            if (typeof Blob !== 'undefined' && nested instanceof Blob) {
                return INLINE_CUSTOMER_MEDIA_OMITTED;
            }
            if (typeof ArrayBuffer !== 'undefined'
                && (nested instanceof ArrayBuffer || ArrayBuffer.isView(nested))) {
                return INLINE_CUSTOMER_MEDIA_OMITTED;
            }
            return nested;
        });
    } catch {
        return _sanitiseTelemetryString(String(value));
    }
}

function _capForDb(value) {
    if (value == null) return null;
    // Pipeline prompts are sometimes objects (we safeClone into JSON strings
    // elsewhere). Stringify defensively so we always write TEXT, never [object].
    const str = typeof value === 'string'
        ? _sanitiseTelemetryString(value)
        : _stringifyTelemetryValue(value);
    if (str.length <= DB_PROMPT_MAX_CHARS) return str;
    const originalLen = str.length;
    return str.slice(0, DB_PROMPT_SAFE_CHARS)
        + `\n\n[... truncated, original length: ${originalLen} chars]`;
}

// Workstream E1 (2026-04-24): static-table cost estimate. Only used when
// OpenRouter did NOT return an authoritative cost. Matches the formula in
// src/services/benchmark-configs.js#computeCostEstimate but operates on a
// single entry so the success-path doesn't need to build an array for one call.
function _estimateCostUsdStatic(model, tokensIn, tokensOut) {
    const pricing = MODEL_PRICING[model] || null;
    if (!pricing) return null; // Unknown model: refuse to guess; see zero-cost warning.
    const costIn = ((tokensIn || 0) / 1_000_000) * pricing.input;
    const costOut = ((tokensOut || 0) / 1_000_000) * pricing.output;
    return Math.round((costIn + costOut) * 1_000_000) / 1_000_000; // 6dp
}

// Workstream E1 silent-$0 guard. When a pipeline finishes with both token
// counts at zero we emit a loud console warning AND a CustomEvent so the
// App shell can surface a visible notice. Previously the cost layer silently
// recorded $0 and a full "successful" harvest run on an all-empty day was
// indistinguishable from a day of real work.
function _emitCostZeroSignal(detail) {
    try {
        if (typeof console !== 'undefined') {
            // eslint-disable-next-line no-console
            console.warn('[PipelineMonitor] qm-cost-zero', detail);
        }
    } catch { /* noop */ }
    try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function'
            && typeof CustomEvent === 'function') {
            window.dispatchEvent(new CustomEvent('qm-cost-zero', { detail }));
        }
    } catch { /* noop */ }
}

// Workstream E1 (retired 2026-05-13 by S4): this frontend Dexie read-then-
// write was a race across tabs + a fire-and-forget backend sync that could
// silently fail. Tsunade's DEC-COUNCIL-001 ruling made ai_usage_events the
// single source of truth for AI cost; migration 041 added a PG trigger
// (ai_usage_event_rollup_to_project) that atomically increments
// projects.ai_cost_cents on every successful event INSERT.
//
// The Dexie row still reflects the latest cost — but it's now pulled FROM
// the backend via the normal sync path, not pushed from this module. That's
// why the function is a no-op now. Kept as a stub so call-sites don't need
// editing; the next-touch deletes the call entirely.
//
// Kill switch (incident-response only): if the trigger misbehaves and you
// need the old behaviour back, set localStorage.qm_use_dexie_cost_rollup
// = 'true' AND revert migration 041 (DOWN script ships with the migration).
async function _addCostToProject(projectId, costUsd) {
    // No-op by design. See header comment + commit 5b01a3xx for S4.
    // Hot path: zero work, zero error surface. Existing callers can keep
    // calling without conditionals.
    void projectId; void costUsd;
}

let entries = [];
let listeners = new Set();
let active = false;
let activeSessionHydrated = false;
let currentProjectId = null; // Set by App.jsx when a project is active
// BQ-CP-7 (2026-05-04): Batch Quote Processor context. When the orchestrator
// is driving a job (`batch-queue.startQueue`), it calls `setBatchContext` on
// every job advance so all subsequent pipeline calls get tagged with the
// batch metadata. Cleared (set to null) when the job completes / queue idles.
// Additive: no caller is required to set this; entries created without a
// batch context just have null fields.
let currentBatchContext = null; // { batchJobId, position, total } | null

// Stage 2's elapsed clock must survive a hard reload, but pipeline inputs and
// outputs can contain customer evidence. Persist only the minimal timing
// envelope for entries that are still running in this browser tab.
function _activeSessionStorage() {
    try {
        return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    } catch {
        return null;
    }
}

function _activeSessionProjectId(value) {
    if (value === null || value === undefined) return null;
    const projectId = String(value).trim();
    return projectId || null;
}

// A recovered timing anchor is deliberately retained so Stage 2 can explain
// when the interrupted browser-owned run began after a hard reload. It is not,
// however, an abortable live request in this JavaScript runtime: reload has
// destroyed its AbortController and the backend records the disconnected
// stream independently. Never let that historical anchor render global
// "AI is running" / STOP controls.
/**
 * @param {Record<string, any> | null | undefined} entry
 * @param {string | null | undefined} projectId
 */
export function isActionableRunningPipelineEntry(entry, projectId = null) {
    if (!entry || entry.status !== 'running' || entry.recoveredTimingAnchor === true) return false;
    const expectedProjectId = _activeSessionProjectId(projectId);
    return !expectedProjectId || _activeSessionProjectId(entry.projectId) === expectedProjectId;
}

function _persistActiveSessionEntries() {
    const storage = _activeSessionStorage();
    if (!storage) return;
    try {
        const running = entries
            .filter(entry => entry?.status === 'running')
            .map(entry => ({
                id: String(entry.id || ''),
                pipelineId: String(entry.pipelineId || 'unknown'),
                stageName: String(entry.stageName || '?'),
                projectId: _activeSessionProjectId(entry.projectId),
                status: 'running',
                startTime: Number(entry.startTime),
            }))
            .filter(entry => entry.id
                && entry.projectId
                && Number.isFinite(entry.startTime)
                && entry.startTime > 0)
            .slice(0, MAX_ENTRIES);
        if (running.length === 0) {
            storage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
        } else {
            storage.setItem(ACTIVE_SESSION_STORAGE_KEY, JSON.stringify(running));
        }
    } catch { /* session storage is optional */ }
}

function _hydrateActiveSessionEntries() {
    if (activeSessionHydrated) return;
    activeSessionHydrated = true;
    const storage = _activeSessionStorage();
    if (!storage) return;
    try {
        const parsed = JSON.parse(storage.getItem(ACTIVE_SESSION_STORAGE_KEY) || '[]');
        if (!Array.isArray(parsed)) return;
        const now = Date.now();
        const existingIds = new Set(entries.map(entry => String(entry?.id || '')));
        const recovered = parsed.filter(entry => {
            const startedAt = Number(entry?.startTime);
            const projectId = _activeSessionProjectId(entry?.projectId);
            return entry?.status === 'running'
                && typeof entry?.id === 'string'
                && entry.id.length > 0
                && projectId !== null
                && !existingIds.has(entry.id)
                && Number.isFinite(startedAt)
                && startedAt > 0
                && startedAt <= now + 5 * 60 * 1000
                && now - startedAt <= ACTIVE_SESSION_MAX_AGE_MS;
        }).map(entry => ({
            id: entry.id,
            pipelineId: String(entry.pipelineId || 'unknown'),
            stageName: String(entry.stageName || '?'),
            projectId: _activeSessionProjectId(entry.projectId),
            group: null,
            originator: null,
            status: 'running',
            startTime: Number(entry.startTime),
            endTime: null,
            duration: null,
            input: null,
            _inputFull: null,
            output: null,
            error: null,
            tokensIn: null,
            tokensOut: null,
            model: null,
            provider: null,
            providerRoute: null,
            providerFallback: null,
            prompt: null,
            recoveredTimingAnchor: true,
        }));
        if (recovered.length > 0) entries = [...recovered, ...entries].slice(0, MAX_ENTRIES);
        _persistActiveSessionEntries();
    } catch {
        try { storage.removeItem(ACTIVE_SESSION_STORAGE_KEY); } catch { /* noop */ }
    }
}

// Safe deep clone that never throws. For large objects, returns a truncated
// JSON string (not parsed back) so the monitor can display something useful
// instead of "[object Object]".
function safeClone(obj, maxLen = 2000) {
    if (obj == null) return null;
    try {
        if (typeof obj === 'string') return _sanitiseTelemetryString(obj).substring(0, maxLen);
        const str = _stringifyTelemetryValue(obj);
        if (str.length <= maxLen) return JSON.parse(str);
        // Return truncated string representation - parsing it would fail
        return str.substring(0, maxLen) + '… (truncated)';
    } catch {
        return String(obj).substring(0, maxLen);
    }
}

function sanitiseProviderAuditObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    try {
        return JSON.parse(JSON.stringify(value, (key, nested) => (
            (SENSITIVE_AUDIT_KEY_RE.test(key)
                || (typeof nested === 'string' && CREDENTIAL_SHAPED_VALUE_RE.test(nested)))
                ? undefined
                : nested
        )));
    } catch {
        return null;
    }
}

function sanitiseProviderFallbackAudit(value) {
    return sanitiseProviderAuditObject(value);
}

function sanitiseProviderRouteAudit(value) {
    const route = sanitiseProviderAuditObject(value);
    if (!route) return null;
    // A diagnosis is rebuilt from the strict allowlist below. Never trust a
    // nested caller-provided diagnosis, even when the remaining route is safe.
    delete route.failureDiagnosis;
    return route;
}

function sanitiseFailureIdentifier(value) {
    if (typeof value !== 'string') return null;
    const identifier = value.trim();
    if (!identifier || identifier.length > FAILURE_IDENTIFIER_MAX_CHARS) return null;
    if (!/^[a-z][a-z0-9._:-]*$/i.test(identifier)) return null;
    return CREDENTIAL_SHAPED_VALUE_RE.test(identifier) ? null : identifier;
}

function sanitiseOperatorAction(value) {
    if (typeof value !== 'string') return null;
    const action = value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!action || action.length > OPERATOR_ACTION_MAX_CHARS) return null;
    return CREDENTIAL_SHAPED_VALUE_RE.test(action) ? null : action;
}

function sanitiseBoundedNumber(value, { min, max, integer = false }) {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < min || numeric > max) return null;
    if (integer && !Number.isInteger(numeric)) return null;
    return numeric;
}

function buildSafeFailureDiagnosis(error, meta = {}) {
    const body = error?.body && typeof error.body === 'object' && !Array.isArray(error.body)
        ? error.body
        : {};
    const value = (metaKey, errorKey = metaKey) => (
        meta?.[metaKey] ?? error?.[errorKey] ?? body?.[errorKey] ?? null
    );
    const diagnosis = {};
    const code = sanitiseFailureIdentifier(value('errorCode', 'code'));
    const category = sanitiseFailureIdentifier(value('errorCategory', 'category'));
    const operatorAction = sanitiseOperatorAction(value('operatorAction'));
    const retryableValue = value('retryable');
    const retryAfterSeconds = sanitiseBoundedNumber(value('retryAfterSeconds'), {
        min: 0,
        max: RETRY_AFTER_MAX_SECONDS,
        integer: true,
    });
    const upstreamStatusCode = sanitiseBoundedNumber(value('upstreamStatusCode'), {
        min: 100,
        max: 599,
        integer: true,
    });
    if (code) diagnosis.code = code;
    if (category) diagnosis.category = category;
    if (operatorAction) diagnosis.operatorAction = operatorAction;
    if (typeof retryableValue === 'boolean') diagnosis.retryable = retryableValue;
    if (retryAfterSeconds !== null) diagnosis.retryAfterSeconds = retryAfterSeconds;
    if (upstreamStatusCode !== null) diagnosis.upstreamStatusCode = upstreamStatusCode;
    return Object.keys(diagnosis).length > 0 ? diagnosis : null;
}

// Provider choice is only final once a request succeeds. Keep it in dedicated
// top-level fields: wrapping `input` to embed this audit used to change a
// truncated prompt string into an object, which hid the full-prompt panel and
// mixed routing evidence into replay input. Migration 084 persists these fields
// directly in Postgres, so the original prompt shape can remain untouched.
function attachRuntimeProviderAudit(entry, meta = {}) {
    const provider = meta.provider ? String(meta.provider) : null;
    const providerRoute = meta.providerRoute
        ? safeClone(sanitiseProviderRouteAudit(meta.providerRoute), 2000)
        : null;
    const providerFallback = meta.providerFallback
        ? safeClone(sanitiseProviderFallbackAudit(meta.providerFallback), 2000)
        : null;
    if (!provider && !providerRoute && !providerFallback) return;

    entry.provider = provider;
    entry.providerRoute = providerRoute;
    entry.providerFallback = providerFallback;
}

function attachRuntimeFailureAudit(entry, error, meta = {}) {
    const diagnosis = buildSafeFailureDiagnosis(error, meta);
    entry.errorCode = diagnosis?.code || null;
    entry.errorCategory = diagnosis?.category || null;
    entry.operatorAction = diagnosis?.operatorAction || null;
    entry.retryable = typeof diagnosis?.retryable === 'boolean' ? diagnosis.retryable : null;
    entry.retryAfterSeconds = diagnosis?.retryAfterSeconds ?? null;
    entry.upstreamStatusCode = diagnosis?.upstreamStatusCode ?? null;
    if (diagnosis) {
        const providerRoute = entry.providerRoute && typeof entry.providerRoute === 'object'
            && !Array.isArray(entry.providerRoute)
            ? entry.providerRoute
            : {};
        entry.providerRoute = { ...providerRoute, failureDiagnosis: diagnosis };
    }
}

export const PipelineMonitor = {
    activate() {
        _hydrateActiveSessionEntries();
        active = true;
    },

    isActive() {
        return active;
    },

    // Set current project context for all subsequent pipeline calls
    setProjectId(projectId) {
        currentProjectId = projectId || null;
    },

    getProjectId() {
        return currentProjectId;
    },

    // BQ-CP-7 (2026-05-04): set the active batch context. The Batch Quote
    // Processor orchestrator (src/services/batch-queue.js) calls this on
    // every job advance so subsequent pipeline entries are tagged with the
    // batch metadata. Null clears the context (e.g. on queue drain).
    setBatchContext(ctx) {
        if (!ctx) {
            currentBatchContext = null;
            return;
        }
        currentBatchContext = {
            batchJobId: ctx.batchJobId ?? null,
            position: ctx.position ?? null,
            total: ctx.total ?? null,
            batchId: ctx.batchId ?? null,
        };
    },

    getBatchContext() {
        return currentBatchContext;
    },

    // Record a pipeline execution start - always safe, never throws.
    // `input` can carry an optional `group` string (e.g. 'diagnostic-pass')
    // so the Developer Tools dashboard can render parallel passes as a
    // bracketed group. The group metadata is purely informational - it does
    // not affect pipeline routing or telemetry aggregation.
    //
    // `input.originator` (added 2026-05-06) lets callers tag an entry with a
    // back-pointer to the surface that triggered it: `{ kind, route, tab,
    // sessionId, label }`. The Pipeline Monitor UI uses this to render a
    // "Open in <surface>" deep-link on the row so the user can jump back to
    // the in-flight workflow they navigated away from. Stored on the entry
    // separately so it survives the `safeClone` truncation of `input`.
    start(pipelineId, stageName, input = null, projectId = null) {
        if (!active) return null;
        try {
            const groupMeta = (input && typeof input === 'object' && typeof input.group === 'string')
                ? input.group
                : null;
            const originator = (input && typeof input === 'object' && input.originator && typeof input.originator === 'object')
                ? {
                    kind: String(input.originator.kind || 'unknown'),
                    route: input.originator.route ? String(input.originator.route) : null,
                    tab: input.originator.tab ? String(input.originator.tab) : null,
                    sessionId: input.originator.sessionId ? String(input.originator.sessionId) : null,
                    label: input.originator.label ? String(input.originator.label) : null,
                }
                : null;
            const entry = {
                id: Date.now() + '_' + Math.random().toString(36).substr(2, 6),
                pipelineId: String(pipelineId || 'unknown'),
                stageName: String(stageName || '?'),
                projectId: projectId || currentProjectId || null,
                group: groupMeta,
                originator,
                status: 'running',
                startTime: Date.now(),
                endTime: null,
                duration: null,
                input: safeClone(input, 1000),
                _inputFull: safeClone(input, 50000),  // full input for export (not displayed in UI)
                output: null,
                error: null,
                tokensIn: null,
                tokensOut: null,
                model: null,
                provider: null,
                providerRoute: null,
                providerFallback: null,
                errorCode: null,
                errorCategory: null,
                operatorAction: null,
                retryable: null,
                retryAfterSeconds: null,
                upstreamStatusCode: null,
                prompt: null,
                // ── Workstream E2 (2026-04-24): additive observability fields ──
                // Populated by success(); null on running + error entries.
                cacheCreationTokens: null,
                cacheReadTokens: null,
                reasoningTokens: null,
                costUsdActual: null,
                costUsdEstimated: null,
                costSource: null, // 'openrouter' | 'bedrock' | 'static' | null
                // ── BQ-CP-7 (2026-05-04): batch quote processor metadata ──
                // Populated when setBatchContext was called before start().
                // Lets PipelineMonitor.jsx group entries by batch job and
                // render a "Batch · #2 of 5" annotation per row.
                batchJobId:    currentBatchContext?.batchJobId ?? null,
                batchPosition: currentBatchContext?.position ?? null,
                batchTotal:    currentBatchContext?.total ?? null,
                batchId:       currentBatchContext?.batchId ?? null,
            };
            entries.unshift(entry);
            if (entries.length > MAX_ENTRIES) entries = entries.slice(0, MAX_ENTRIES);
            notify();
            return entry.id;
        } catch (e) {
            console.warn('[PipelineMonitor] start() error:', e);
            return null;
        }
    },

    // Record pipeline success - always safe
    success(entryId, output, meta = {}) {
        if (!active || !entryId) return;
        try {
            const entry = entries.find(e => e.id === entryId);
            if (!entry) return;
            if (entry.status === 'cancelled') return;
            entry.status = 'success';
            entry.endTime = Date.now();
            entry.duration = entry.endTime - entry.startTime;
            entry.output = safeClone(output, 500);
            entry._outputFull = safeClone(output, 50000);  // full output for export
            entry.tokensIn = meta.tokensIn || null;
            entry.tokensOut = meta.tokensOut || null;
            entry.model = meta.model || null;
            entry.prompt = meta.prompt ? String(meta.prompt).substring(0, 200) : null;
            // ── Workstream E2: cache + reasoning + cost fields ─────────────
            // Every field is ADDITIVE. Absent meta keys leave them null,
            // preserving the display contract for legacy callers.
            entry.cacheCreationTokens = (meta.cacheCreationTokens != null) ? meta.cacheCreationTokens : null;
            entry.cacheReadTokens = (meta.cacheReadTokens != null) ? meta.cacheReadTokens : null;
            entry.reasoningTokens = (meta.reasoningTokens != null) ? meta.reasoningTokens : null;
            attachRuntimeProviderAudit(entry, meta);

            // ── Workstream E1: cost resolution ─────────────────────────────
            // OpenRouter-reported actual cost wins; static-table is the
            // fallback so the column is never silently $0 when tokens
            // landed. The zero-token path emits a loud event instead of
            // recording a phantom cost.
            const tokensInSafe = Number(entry.tokensIn) || 0;
            const tokensOutSafe = Number(entry.tokensOut) || 0;
            const costActual = (typeof meta.costUsd === 'number' && Number.isFinite(meta.costUsd) && meta.costUsd >= 0)
                ? meta.costUsd
                : null;
            const costEstimated = _estimateCostUsdStatic(entry.model, tokensInSafe, tokensOutSafe);
            entry.costUsdActual = costActual;
            entry.costUsdEstimated = costEstimated;
            let resolvedCost = null;
            let resolvedSource = null;
            if (costActual != null) {
                resolvedCost = costActual;
                resolvedSource = entry.provider === 'bedrock' ? 'bedrock' : 'openrouter';
            } else if (costEstimated != null) {
                resolvedCost = costEstimated;
                resolvedSource = 'static';
            }
            entry.costSource = resolvedSource;

            if (tokensInSafe === 0 && tokensOutSafe === 0) {
                // No tokens = no cost. v5.16.24: skip the anomaly emit when
                // the entry is a deterministic (non-AI) step — those legitimately
                // have zero cost (rfq-preflight-gate, cost-recompute math passes,
                // etc.) and the warning is just log noise. The model name
                // "deterministic" is the canonical sentinel set by every
                // deterministic call site. Real $0 anomalies (a model that
                // returned zero tokens despite being a real provider) still
                // surface as before.
                const isDeterministic = String(entry.model || '').toLowerCase() === 'deterministic';
                if (!isDeterministic) {
                    _emitCostZeroSignal({
                        entryId: entry.id,
                        pipelineId: entry.pipelineId,
                        stageName: entry.stageName,
                        model: entry.model,
                        projectId: entry.projectId,
                    });
                }
                resolvedCost = null;
                resolvedSource = null;
                entry.costSource = null;
            }

            // Roll the authoritative cost up to the owning project. Fire-and-
            // forget: guarded + idempotent.
            if (resolvedCost != null && entry.projectId) {
                _addCostToProject(entry.projectId, resolvedCost);
            }

            notify();
            // Persist to DataService (non-blocking). `silent: true` so every
            // pipeline step doesn't spam the toast UI during a backend outage.
            // the reconnection banner already tells the user the backend is
            // down. Failures still log to the console for triage.
            if (!isEvalAiSingleAttemptPolicyActive()) {
                try {
                    const successRow = {
                        projectId: entry.projectId, pipelineId: entry.pipelineId,
                        stageName: entry.stageName, status: 'success',
                        startTime: new Date(entry.startTime).toISOString(),
                        endTime: new Date(entry.endTime).toISOString(),
                        duration: entry.duration,
                        model: entry.model, tokensIn: entry.tokensIn, tokensOut: entry.tokensOut,
                        // Workstream E2: extra token buckets + cost fields.
                        // The backend INSERT names them cache_creation_tokens /
                        // cache_read_tokens / reasoning_tokens / cost_usd_actual /
                        // cost_usd_estimated / cost_source; the route maps from
                        // these camelCase aliases.
                        cacheCreationTokens: entry.cacheCreationTokens,
                        cacheReadTokens: entry.cacheReadTokens,
                        reasoningTokens: entry.reasoningTokens,
                        costUsdActual: entry.costUsdActual,
                        costUsdEstimated: entry.costUsdEstimated,
                        costSource: entry.costSource,
                        provider: entry.provider,
                        providerRoute: entry.providerRoute,
                        providerFallback: entry.providerFallback,
                        // Workstream E3: full prompt + response, capped to
                        // 200k chars at the app layer. _inputFull / _outputFull
                        // are our 50k-char clones; stringify them through the
                        // DB helper so the cap is consistent whether the
                        // object is already a string or still structured.
                        prompt: _capForDb(entry._inputFull),
                        // Persist from the original success payload, not the
                        // memory-bounded 50k Developer Tools copy. This keeps
                        // the browser tab bounded while the durable trace can
                        // retain up to the existing 200k telemetry boundary.
                        response: _capForDb(output),
                        temperature: meta.temperature || null,
                    };
                    _persistPipelineRow(entry.id, () => safeWriteFF(
                        DataService.pipelines.save(successRow),
                        { what: 'pipelines.save(success)', silent: true, context: { pipelineId: entry.pipelineId, stageName: entry.stageName } },
                    ));
                } catch {}
                // Record performance telemetry on the current prompt version (Phase 5).
                // Evaluation mode must not schedule detached persistence that can
                // outlive and escape its mutation guard.
                try {
                    import('../config/pipeline-registry.js').then(({ recordPromptPerformance }) => {
                        recordPromptPerformance(entry.pipelineId, {
                            inputTokens: entry.tokensIn,
                            outputTokens: entry.tokensOut,
                            latencyMs: entry.duration,
                            success: true,
                        }).catch(() => {}); // Fire-and-forget outside evaluator only
                    }).catch(() => {});
                } catch {}
            }
        } catch (e) {
            console.warn('[PipelineMonitor] success() error:', e);
        }
    },

    // Record pipeline error - always safe.
    //
    // Callers can pass `meta.cancelled = true` when the error was caused by
    // a user-initiated AbortController cancellation (e.g. clicking the
    // Cancel button on a cost-recompute overlay). Cancelled runs get their
    // OWN status ('cancelled'), not 'error', so the UI can render them as
    // a neutral/muted state instead of a red alarm - this matters at
    // production scale where hundreds of users may cancel thousands of
    // recomputes a day and we don't want the dashboard to look on fire.
    error(entryId, error, meta = {}) {
        if (!active || !entryId) return;
        try {
            const entry = entries.find(e => e.id === entryId);
            if (!entry) return;
            if (entry.status === 'cancelled' && meta?.cancelled !== true) return;
            const isCancelled = meta && meta.cancelled === true;
            entry.status = isCancelled ? 'cancelled' : 'error';
            entry.endTime = Date.now();
            entry.duration = entry.endTime - entry.startTime;
            entry.error = error?.message || String(error || 'Unknown error').substring(0, 500);
            entry.model = meta.model || null;
            entry.prompt = meta.prompt ? String(meta.prompt).substring(0, 200) : null;
            entry.cancelled = isCancelled; // explicit flag for filters/queries
            attachRuntimeProviderAudit(entry, meta);
            attachRuntimeFailureAudit(entry, error, meta);
            notify();
            // Persist to DataService (non-blocking). `silent: true` so error-
            // telemetry writes don't spam toasts during a backend outage.
            if (!isEvalAiSingleAttemptPolicyActive()) {
                try {
                    const errorRow = {
                        projectId: entry.projectId, pipelineId: entry.pipelineId,
                        stageName: entry.stageName, status: entry.status,
                        startTime: new Date(entry.startTime).toISOString(),
                        endTime: new Date(entry.endTime).toISOString(),
                        duration: entry.duration,
                        model: entry.model,
                        provider: entry.provider,
                        providerRoute: entry.providerRoute,
                        providerFallback: entry.providerFallback,
                        error: entry.error,
                        // Workstream E3: persist the prompt on failed runs so
                        // they can be replayed. Response is usually null on an
                        // error path; cap it defensively anyway.
                        prompt: _capForDb(entry._inputFull),
                        response: _capForDb(entry._outputFull),
                    };
                    _persistPipelineRow(entry.id, () => safeWriteFF(
                        DataService.pipelines.save(errorRow),
                        { what: 'pipelines.save(error)', silent: true, context: { pipelineId: entry.pipelineId, stageName: entry.stageName } },
                    ));
                } catch {}
            }
        } catch (e) {
            console.warn('[PipelineMonitor] error() error:', e);
        }
    },

    cancelRunningForProject(projectId, reason = 'Cancelled by user') {
        if (!active) return 0;
        try {
            const pid = projectId || currentProjectId || null;
            const now = Date.now();
            let cancelled = 0;
            for (const entry of entries) {
                if (!entry || entry.status !== 'running') continue;
                if (pid && entry.projectId !== pid) continue;
                entry.status = 'cancelled';
                entry.endTime = now;
                entry.duration = entry.startTime ? now - entry.startTime : null;
                entry.error = String(reason || 'Cancelled by user').substring(0, 500);
                entry.cancelled = true;
                cancelled += 1;
                if (!isEvalAiSingleAttemptPolicyActive()) {
                    try {
                        const cancelledRow = {
                            projectId: entry.projectId,
                            pipelineId: entry.pipelineId,
                            stageName: entry.stageName,
                            status: 'cancelled',
                            startTime: entry.startTime ? new Date(entry.startTime).toISOString() : null,
                            endTime: new Date(entry.endTime).toISOString(),
                            duration: entry.duration,
                            model: entry.model,
                            provider: entry.provider,
                            providerRoute: entry.providerRoute,
                            providerFallback: entry.providerFallback,
                            error: entry.error,
                            prompt: _capForDb(entry._inputFull),
                            response: _capForDb(entry._outputFull),
                        };
                        _persistPipelineRow(entry.id, () => safeWriteFF(
                            DataService.pipelines.save(cancelledRow),
                            { what: 'pipelines.save(cancelled)', silent: true, context: { pipelineId: entry.pipelineId, stageName: entry.stageName } },
                        ));
                    } catch {}
                }
            }
            if (cancelled > 0) notify();
            return cancelled;
        } catch (e) {
            console.warn('[PipelineMonitor] cancelRunningForProject() error:', e);
            return 0;
        }
    },

    getEntries() {
        return entries;
    },

    clear() {
        entries = [];
        notify();
    },

    subscribe(callback) {
        listeners.add(callback);
        return () => listeners.delete(callback);
    },

    getStats() {
        try {
            const total = entries.length;
            const success = entries.filter(e => e.status === 'success').length;
            const errors = entries.filter(e => e.status === 'error').length;
            const running = entries.filter(e => e.status === 'running').length;
            const cancelled = entries.filter(e => e.status === 'cancelled').length;
            const completed = entries.filter(e => e.status === 'success' && e.duration);
            const avgDuration = completed.length > 0
                ? Math.round(completed.reduce((s, e) => s + e.duration, 0) / completed.length)
                : 0;
            return { total, success, errors, running, cancelled, avgDuration };
        } catch {
            return { total: 0, success: 0, errors: 0, running: 0, cancelled: 0, avgDuration: 0 };
        }
    },

    /**
     * Export the full pipeline log as a JSON object suitable for debugging.
     * Includes every entry with full detail, session stats, timing analysis,
     * and the diagnosis telemetry if available.
     */
    exportFullLog() {
        try {
            const stats = this.getStats();
            const completedEntries = entries.filter(e => e.status === 'success' && e.duration);
            const allEntries = entries.map(e => ({
                id: e.id,
                pipelineId: e.pipelineId,
                stageName: e.stageName,
                projectId: e.projectId,
                group: e.group || null,
                status: e.status,
                startTime: e.startTime ? new Date(e.startTime).toISOString() : null,
                endTime: e.endTime ? new Date(e.endTime).toISOString() : null,
                durationMs: e.duration || null,
                model: e.model || null,
                provider: e.provider || null,
                providerRoute: e.providerRoute || null,
                providerFallback: e.providerFallback || null,
                errorCode: e.errorCode || null,
                errorCategory: e.errorCategory || null,
                operatorAction: e.operatorAction || null,
                retryable: typeof e.retryable === 'boolean' ? e.retryable : null,
                retryAfterSeconds: e.retryAfterSeconds ?? null,
                upstreamStatusCode: e.upstreamStatusCode ?? null,
                tokensIn: e.tokensIn || null,
                tokensOut: e.tokensOut || null,
                // UI-truncated versions (1000/500 chars) for quick scanning
                inputPreview: e.input || null,
                outputPreview: e.output || null,
                // Full-length versions for debugging (up to 50k chars each)
                inputFull: e._inputFull || e.input || null,
                outputFull: e._outputFull || e.output || null,
                error: e.error || null,
                cancelled: e.cancelled || false,
            }));

            // Group by stage for readability
            const stageOrder = { '1': 1, '1.5': 2, '1.75': 3, '2': 4, '2-background': 5, '3': 6, '4': 7 };
            const stageGroups = {};
            for (const entry of allEntries) {
                const stageKey = entry.stageName?.match(/^[\d.]+/)?.[0] || entry.stageName || 'unknown';
                if (!stageGroups[stageKey]) stageGroups[stageKey] = [];
                stageGroups[stageKey].push(entry);
            }

            // Compute critical path (longest chain of sequential calls)
            const sortedByStart = [...completedEntries].sort((a, b) => a.startTime - b.startTime);
            const firstStart = sortedByStart[0]?.startTime || Date.now();
            const lastEnd = Math.max(...entries.map(e => e.endTime || 0), 0);
            const wallClockMs = lastEnd > firstStart ? lastEnd - firstStart : 0;

            return {
                exportVersion: '1.0',
                exportedAt: new Date().toISOString(),
                projectId: currentProjectId || null,
                session: {
                    ...stats,
                    wallClockMs,
                    firstCallStart: firstStart ? new Date(firstStart).toISOString() : null,
                    lastCallEnd: lastEnd ? new Date(lastEnd).toISOString() : null,
                    totalTokensIn: entries.reduce((s, e) => s + (e.tokensIn || 0), 0),
                    totalTokensOut: entries.reduce((s, e) => s + (e.tokensOut || 0), 0),
                },
                stageGroups,
                entries: allEntries,
            };
        } catch (err) {
            console.error('[PipelineMonitor] Export failed:', err);
            return { error: err.message, entries: [] };
        }
    },

    /**
     * Download the full log as a JSON file. Merges diagnosis telemetry
     * if the module is loaded. Uses async dynamic import (not require).
     */
    async downloadLog(filename) {
        try {
            const log = this.exportFullLog();

            const blob = new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `qm-pipeline-log-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('[PipelineMonitor] Download failed:', err);
        }
    },

    // Caller-owned cancellation controls live AI requests. Do not manufacture
    // a five-minute failure while a legitimate Stage 2/3 inference is still
    // running; expire only abandoned tab-session anchors after one day.
    cleanup() {
        const expiryCutoff = Date.now() - ACTIVE_SESSION_MAX_AGE_MS;
        entries = entries.map(e => {
            if (e.status === 'running' && e.startTime < expiryCutoff) {
                return { ...e, status: 'error', error: 'Abandoned session timing anchor expired', endTime: Date.now(), duration: Date.now() - e.startTime };
            }
            return e;
        });
        notify();
    }
};

function notify() {
    _persistActiveSessionEntries();
    // Use setTimeout to prevent synchronous cascading updates
    setTimeout(() => {
        listeners.forEach(cb => {
            try { cb([...entries]); } catch (e) { console.warn('[PipelineMonitor] listener error:', e); }
        });
    }, 0);
}

// Auto-cleanup abandoned tab-session anchors without imposing an AI timeout.
setInterval(() => {
    if (active && entries.some(e => e.status === 'running' && Date.now() - e.startTime > ACTIVE_SESSION_MAX_AGE_MS)) {
        PipelineMonitor.cleanup();
    }
}, 120000);
